import { calculatePercentage, calculatePercentageChange, calculateSentiment } from '@/pages/agents/utils';
import { GetInteractionsAggregatesResponse } from '@d-id/types';

export type InsightsSentiment = 'positive' | 'negative' | 'neutral';

export interface ProcessedInsightsUserTopicsItem {
    topic: string;
    intent: string;
    n_sessions: number;
    sessions_change: number;
    sessions_sentiment: InsightsSentiment;
    interactions: number;
    interactions_change: number;
    interactions_sentiment: InsightsSentiment;
    users: number;
    users_change: number;
    users_sentiment: InsightsSentiment;
    total_interactions: number;
    positive_conversation_sentiment_percentage: number;
    negative_conversation_sentiment_percentage: number;
}

export interface ProcessedInsightsUserTopics {
    topics: string[];
    intents: string[];
    items: ProcessedInsightsUserTopicsItem[];
}

export const processInsightsUserTopics = (data: GetInteractionsAggregatesResponse | undefined): ProcessedInsightsUserTopics => {
    const topics = Array.from(new Set(data?.data.map(item => item.group_name)));
    const intents = Array.from(new Set(data?.data.map(item => item.additional_groups?.[0]).filter(Boolean) as string[]));

    const totalInteractions = data?.data.reduce((acc, bucket) => acc + bucket.n_interactions || 0, 0) || 0;

    const items =
        data?.data.map(item => {
            const interactionsChange = calculatePercentageChange(item.n_interactions, item.n_interactions_prev) || 0;
            const interactionsSentiment = calculateSentiment(interactionsChange);

            const sessionsChange = calculatePercentageChange(item.n_sessions, item.n_sessions_prev) || 0;
            const sessionsSentiment = calculateSentiment(sessionsChange);

            const usersChange = calculatePercentageChange(item.n_users, item.n_users_prev) || 0;
            const usersSentiment = calculateSentiment(usersChange);

            const positiveConversationSentimentPercentage = calculatePercentage(item.n_conversations_positive / item.n_sessions);
            const negativeConversationSentimentPercentage = calculatePercentage(item.n_conversations_negative / item.n_sessions);

            return {
                topic: item.group_name,
                intent: item.additional_groups?.[0] || '',
                n_sessions: item.n_sessions,
                sessions_change: sessionsChange,
                sessions_sentiment: sessionsSentiment,
                interactions: item.n_interactions,
                interactions_change: interactionsChange,
                interactions_sentiment: interactionsSentiment,
                users: item.n_users,
                users_change: usersChange,
                users_sentiment: usersSentiment,
                positive_conversation_sentiment_percentage: positiveConversationSentimentPercentage,
                negative_conversation_sentiment_percentage: negativeConversationSentimentPercentage,
                total_interactions: calculatePercentage(item.n_interactions / totalInteractions),
            };
        }) || [];

    return {
        topics,
        intents,
        items,
    };
};
